/*
 * O1/O3 (Erwin 2026-10-29): a Helm XR web window cannot use a passkey (GeckoView has no FIDO2),
 * so platform-admin power is confirmed by a step-up code approved with a passkey in Helm on another
 * device (phone/Mac, or Chrome on the Beam Pro). "Use this headset" (O2) is never offered for admin:
 * the gateway sets `admin` in the answer and the glasses' chooser hides it.
 * Same flow as Helm web Web/src/pages/HandoffStepUp.tsx.
 */

import { useEffect, useRef, useState } from 'react';
import { authCall, recoverAfterUsed } from '../lib/authCall';
import { hello, onStepUpCancelled, requestStepUp, stepUpDone } from '../lib/glasses';
import { abortableSleep, pollRequest, pollStepUp, startHandoffStepUp, type StepUpStart } from '../lib/handoffStepup';

type Phase =
  | { k: 'idle' }
  | { k: 'starting' }
  | { k: 'waiting'; start: StepUpStart; glasses: boolean }
  | { k: 'done' }
  | { k: 'failed'; message: string };

/** `onToken` stores the stepped-up access token (the refresh token is kept) and re-reads /auth/me. */
export default function HandoffStepUp({ onToken }: { onToken: (accessToken: string) => Promise<void> }) {
  const [phase, setPhase] = useState<Phase>({ k: 'idle' });
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => { abort.current?.abort(); }, []);

  async function begin() {
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setPhase({ k: 'starting' });
    const [res, bridge] = await Promise.all([startHandoffStepUp(authCall), hello()]);
    if (ctl.signal.aborted) return;
    if (!res.ok) { setPhase({ k: 'failed', message: res.message }); return; }
    const { start } = res;
    const off = onStepUpCancelled(() => ctl.abort());
    if (bridge) {
      requestStepUp({ userCode: start.userCode, verificationUri: start.verificationUri, expiresIn: start.expiresIn, admin: start.admin, headset: start.headset });
    }
    setPhase({ k: 'waiting', start, glasses: !!bridge });
    const out = await pollStepUp(start, { poll: pollRequest(authCall, start.stepupCode), sleep: abortableSleep, signal: ctl.signal, recover: recoverAfterUsed });
    off();
    if (abort.current !== ctl) return;
    if (out.kind === 'ok') {
      if (bridge) stepUpDone();
      setPhase({ k: 'done' });
      await onToken(out.accessToken);
      return;
    }
    setPhase({ k: 'failed', message: out.message });
  }

  if (phase.k === 'idle' || phase.k === 'starting') {
    return (
      <>
        <p className="text-muted small">This Helm XR window cannot use a passkey itself. Approve with your passkey in Helm on your phone or Mac.</p>
        <button type="button" className="btn btn-primary" disabled={phase.k === 'starting'} onClick={() => void begin()}>
          {phase.k === 'starting' ? 'Starting…' : 'Confirm it’s you'}
        </button>
      </>
    );
  }
  if (phase.k === 'waiting') {
    const { start, glasses } = phase;
    return glasses ? (
      <>
        <p className="mb-1">Choose how to confirm in the glasses…</p>
        <p className="text-muted small mb-0">Code: <code>{start.userCode}</code></p>
      </>
    ) : (
      <>
        <p className="text-muted small mb-1">Approve on your phone or Mac with your passkey:</p>
        <p className="h3 font-monospace mb-1">{start.userCode}</p>
        <p className="small"><a href={start.verificationUri} target="_blank" rel="noreferrer">{start.verificationUri}</a></p>
        <p className="text-muted small mb-0">Waiting for the approval…</p>
      </>
    );
  }
  if (phase.k === 'done') return <p className="text-muted small mb-0">Confirmed.</p>;
  return (
    <>
      <div className="alert alert-warning small">{phase.message}</div>
      <button type="button" className="btn btn-primary" onClick={() => void begin()}>Try again</button>
    </>
  );
}
